import { Box, Card, Flex, Grid, Text, TextInput, Title } from '@mantine/core';
import { IconChartPieOff, IconSearch } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import AbTestCard from '../../../components/AbTest/AbTestCard';
import ErrorMessage from '../../../components/ErrorMessage';
import Metadata from '../../../components/Metadata';
import NewElementButton from '../../../components/NewElementButton';
import Spinner from '../../../components/Spinner';
import { FilterListByName } from '../../../constants/filterListByName';
import { Page } from '../../../constants/pageTitles';
import useFetch from '../../../helpers/useFetch';

const AbTestsPage = () => {
  const fetch = useFetch();
  const { accountId, domainId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [abTests, setAbTests] = useState([]);
  const [runningAbTests, setRunningAbTests] = useState([]);
  const [allAbTests, setAllAbTests] = useState([]);
  const [error, setError] = useState();

  const fetchUrl = `/api/admin/accounts/${accountId}/domains/${domainId}/abTests`;

  useEffect(() => {
    setAbTests(undefined);
    fetch(fetchUrl)
      .then(({ abTests }) => {
        setAbTests(abTests.filter((test) => test.running !== true));
        setAllAbTests(abTests.filter((test) => test.running !== true));
        setRunningAbTests(abTests.filter((test) => test.running === true));
      })
      .catch(setError);
  }, [fetch, fetchUrl]);

  const searchHandler = (term) => {
    if (term === '') {
      setAbTests(allAbTests);
    } else {
      setAbTests(FilterListByName(term, allAbTests));
    }
  };

  const onSubmit = (event) => {
    event.preventDefault();
  };

  const stopAbTest = (abTestId) => {
    const abTest = runningAbTests.find((test) => test.id === abTestId);

    fetch(`${fetchUrl}/${abTest.id}`, {
      method: 'PUT',
      body: JSON.stringify({ ...abTest, running: false }),
    })
      .then(({ abTest }) => {
        setRunningAbTests(runningAbTests.filter((test) => test.id !== abTest.id));
        setAbTests([abTest, ...abTests]);
        setAllAbTests([abTest, ...allAbTests]);
      })
      .catch(setError);
  };

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  if (!abTests) {
    return <Spinner />;
  }

  const abTestCardsElements = abTests.map((abTest) => <AbTestCard key={abTest.id} abTest={abTest} />);

  const runningAbTestCardsElements = runningAbTests.map((abTest) => (
    <AbTestCard key={abTest.id} abTest={abTest} stopAbTest={stopAbTest} />
  ));

  const noAbTestDisplayMessage = (
    <Text c="dimmed" ta="center">
      <IconChartPieOff size={24} />
      <Title c="dimmed" order={3}>
        No active A/B test
      </Title>
    </Text>
  );
  const noPreviousAbTestsDisplayMessage = (
    <Text c="dimmed" ta="center">
      <IconChartPieOff size={24} />
      <Title c="dimmed" order={3}>
        No previous A/B tests found
      </Title>
    </Text>
  );

  return (
    <div>
      <Metadata title={`${Page['abTests']}`} />
      <Grid>
        <Grid.Col span={12} ta="right"></Grid.Col>
      </Grid>
      <Flex component="form" className="search-form" style={{ marginBottom: 'var(--mantine-spacing-sm)' }} onSubmit={onSubmit}>
        <TextInput
          onKeyUp={(event) => searchHandler(event.target.value)}
          type="search"
          placeholder="Search"
          mr="sm"
          aria-label="Search"
          rightSection={<IconSearch size={16} />}
        />
      </Flex>
      <Grid>
        <Grid.Col span={12}>
          <Text>Running A/B tests</Text>
        </Grid.Col>
      </Grid>
      <Grid>
        <Grid.Col span={12} mb="lg">
          <Card withBorder>
            {runningAbTests.length > 0 ? runningAbTestCardsElements : noAbTestDisplayMessage}
            <Box ta="center" mt="lg">
              <NewElementButton
                size="xl"
                onClick={() => navigate(location.pathname + '/new' + location.search)}
                title="Start A/B Test"
                disabled={runningAbTests.length > 0}
              />
            </Box>
          </Card>
        </Grid.Col>
      </Grid>
      <Grid>
        <Grid.Col span={12}>{abTests.length === 0 && noPreviousAbTestsDisplayMessage}</Grid.Col>
      </Grid>

      {abTestCardsElements}
    </div>
  );
};

export default AbTestsPage;
